<template>
  <div class="relative" :class="marginClass">
    <!-- Two layers, see the note above the props below. -->
    <div v-if="src" :class="['relative rounded-full', sizeClass]">
      <span
        :class="[
          'absolute inset-0 flex items-center justify-center rounded-full bg-[#014451] text-uppercase text-white',
          textClass
        ]"
        >{{ initials }}</span
      >
      <span
        class="absolute inset-0 rounded-full bg-no-repeat bg-cover bg-center"
        :style="{ backgroundImage: `url(${src})` }"
      ></span>
    </div>
    <div
      v-else
      :class="[
        'bg-[#014451] text-uppercase flex items-center justify-center rounded-full text-white',
        textClass,
        sizeClass
      ]"
    >
      {{ initials }}
    </div>
    <span
      v-if="statusClass && timeCardsEnabled"
      class="clocked-in-status-icon absolute w-3.5 h-3.5 border-2 border-white dark:border-gray-800 rounded-full"
      :class="statusClass"
    ></span>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useStore } from "vuex";

const store = useStore();

// The photo layer sits ON TOP of an initials layer rather than replacing it.
// A CSS background fires no error event, so a src that 404s -- or whose
// presigned URL has expired -- cannot be detected, and the previous markup drew
// that case as an empty, colourless circle. Layering means a working photo
// covers the initials exactly as before, while a broken one falls through to
// the same circle the no-src branch already draws.
//
// The rationale lives here rather than beside the markup because a template
// comment is kept in the rendered HTML, and ScheduleColumnHeader's spec asserts
// the no-src render carries no CSS background -- prose naming it there fails
// that assertion without any behaviour changing.
const props = withDefaults(
  defineProps<{
    src?: string;
    alt?: string;
    status?: string;
    marginClass?: string;
    initials?: string;
    sizeClass?: string;
    // The initials' type scale. Split from the hardcoded `text-lg` so a caller
    // that shrinks `sizeClass` can shrink the text with it -- Tailwind emits
    // utilities as `!important` here, so a second text-* class in `sizeClass`
    // could never win against a hardcoded one.
    textClass?: string;
  }>(),
  {
    src: "",
    alt: "",
    status: "",
    marginClass: "",
    initials: "",
    sizeClass: "w-10 h-10",
    textClass: "text-lg"
  }
);
const statusClass = computed(() => {
  if (props.status === "online") {
    return "top-0 start-7 bg-green-500";
  }
  if (props.status === "offline") {
    return "top-0 start-7 bg-red-500";
  }
  return "";
});

const timeCardsEnabled = computed(() => store.state.tenant?.time_cards_enabled);
</script>
